import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase";
import Navbar from "../Navbar/Navbar";
import "./Settings.css";

const Settings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState({
    bookings: true,
    messages: true,
    marketing: true,
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        const { data: profile } = await supabase
          .from("user_profiles")
          .select("full_name, email_notifications")
          .eq("user_id", user.id)
          .single();

        setUserData({ email: user.email, ...profile });

        // Load notification preferences if they exist, defaulting marketing to true
        if (profile?.email_notifications) {
          setEmailNotifications({
            bookings: true,
            messages: true,
            marketing: true,
            ...profile.email_notifications,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
    setLoading(false);
  };

  const handleNotificationChange = async (type) => {
    const newPreferences = {
      ...emailNotifications,
      [type]: !emailNotifications[type],
    };
    setEmailNotifications(newPreferences);

    // Save to database
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      await supabase
        .from("user_profiles")
        .update({ email_notifications: newPreferences })
        .eq("user_id", user.id);
    } catch (error) {
      console.error("Error saving notification preferences:", error);
    }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") {
      return;
    }

    setDeleting(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("No user found");
      }

      // Soft delete: mark account as deleted so it disappears from Explore/Saved
      const { error: updateError } = await supabase
        .from("user_profiles")
        .update({ account_status: "deleted", skill_provider: false, skill_active: false })
        .eq("user_id", user.id);

      if (updateError) {
        throw updateError;
      }

      // Sign out
      await supabase.auth.signOut();

      // Redirect to landing page
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("There was an error deleting your account. Please contact support.");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#fafafa" }}>
        <Navbar />
        <div
          style={{
            padding: "2rem",
            textAlign: "center",
            fontSize: "15px",
            color: "#525252",
          }}
        >
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#fafafa",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <Navbar />

      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          padding: "2rem 1rem",
        }}
      >
        <h1
          style={{
            fontSize: "28px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "2rem",
          }}
        >
          Settings
        </h1>

        {/* Notifications Section */}
        <div className="settings-section">
          <h2 className="settings-section-title">Notifications</h2>
          <div className="settings-card">
            <div className="settings-item">
              <div>
                <h3 className="settings-item-title">Booking Notifications</h3>
                <p className="settings-item-description">
                  Receive emails when you get new booking requests
                </p>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={emailNotifications.bookings}
                  onChange={() => handleNotificationChange("bookings")}
                  disabled={saving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            <div className="settings-divider"></div>

            <div className="settings-item">
              <div>
                <h3 className="settings-item-title">Message Notifications</h3>
                <p className="settings-item-description">
                  Receive emails when you get new messages
                </p>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={emailNotifications.messages}
                  onChange={() => handleNotificationChange("messages")}
                  disabled={saving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            <div className="settings-divider"></div>

            <div className="settings-item">
              <div>
                <h3 className="settings-item-title">Marketing Emails</h3>
                <p className="settings-item-description">
                  Receive updates, tips, and news about Collavo
                </p>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={emailNotifications.marketing}
                  onChange={() => handleNotificationChange("marketing")}
                  disabled={saving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Account Section */}
        <div className="settings-section">
          <h2 className="settings-section-title">Account</h2>
          <div className="settings-card">
            <div className="settings-item">
              <div>
                <h3 className="settings-item-title">Email Address</h3>
                <p className="settings-item-description">{userData?.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="settings-section">
          <h2
            className="settings-section-title"
            style={{ color: "#dc2626" }}
          >
            Danger Zone
          </h2>
          <div className="settings-card" style={{ borderColor: "#fecaca" }}>
            <div className="settings-item">
              <div>
                <h3 className="settings-item-title" style={{ color: "#dc2626" }}>
                  Delete Account
                </h3>
                <p className="settings-item-description">
                  Permanently delete your account and all associated data. This
                  action cannot be undone.
                </p>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="settings-button-danger"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="settings-modal-overlay">
          <div className="settings-modal">
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#1a1a1a",
                marginBottom: "1rem",
              }}
            >
              Delete Account
            </h2>
            <p
              style={{
                fontSize: "15px",
                color: "#525252",
                marginBottom: "1.5rem",
                lineHeight: "1.5",
              }}
            >
              This will permanently delete your account and all associated data,
              including:
            </p>
            <ul
              style={{
                fontSize: "14px",
                color: "#525252",
                marginBottom: "1.5rem",
                paddingLeft: "1.5rem",
              }}
            >
              <li>Your profile and service offerings</li>
              <li>All expertise and pricing information</li>
              <li>Booking history</li>
              <li>Message history</li>
            </ul>
            <p
              style={{
                fontSize: "15px",
                color: "#dc2626",
                fontWeight: "500",
                marginBottom: "1rem",
              }}
            >
              This action cannot be undone.
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#525252",
                marginBottom: "0.5rem",
              }}
            >
              Type <strong>DELETE</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "15px",
                border: "1px solid #e5e5e5",
                borderRadius: "8px",
                marginBottom: "1.5rem",
                boxSizing: "border-box",
                backgroundColor: "#fff",
                color: "#1a1a1a",
              }}
            />
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText("");
                }}
                className="settings-button-secondary"
                disabled={deleting}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="settings-button-danger"
                disabled={deleteConfirmText !== "DELETE" || deleting}
                style={{ flex: 1, opacity: deleteConfirmText !== "DELETE" ? 0.5 : 1 }}
              >
                {deleting ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
